class InstinctProductForm extends HTMLElement {
  connectedCallback() {
    if (this._initialized) return;
    this._initialized = true;

    this._abortController?.abort();
    this._abortController = new AbortController();
    const { signal } = this._abortController;

    this.cards = this.querySelectorAll('[data-order-type]');
    this.selectButtons = this.querySelectorAll('[data-instinct-select]');
    this.planInput = this.querySelector('[data-instinct-form-plan-input]');
    this.planSelect = this.querySelector('[data-instinct-form-plan-select]');
    this.quantityInput = this.querySelector('[data-instinct-form-quantity-input]');
    this.quantitySelects = this.querySelectorAll('[data-instinct-form-quantity]');
    this.defaultPlanId = this.dataset.defaultPlan || '';
    this.defaultMode = this.dataset.defaultMode || 'subscribe';
    this.currentMode = this.defaultMode;
    this._canAddToCart = this.dataset.canAddToCart !== 'false';

    this.selectButtons.forEach((button) => {
      button.addEventListener('click', this._onSelect.bind(this), { signal });
    });

    if (this.planSelect) {
      this.planSelect.addEventListener('change', this._onPlanChange.bind(this), { signal });
    }

    this.quantitySelects.forEach((select) => {
      select.addEventListener('change', this._onQuantityChange.bind(this), { signal });
    });

    // Intercept form submission for AJAX cart
    const form = this.querySelector('form');
    if (form) {
      form.addEventListener('submit', this._onFormSubmit.bind(this), { signal });
    }

    // Restore button enabled state if a third-party app (e.g. Skio) disables it
    this._watchSubmitButtons();

    // Update availability when the selected variant changes
    document.addEventListener('variant:update', this._onVariantUpdate.bind(this), { signal });

    this._syncMode(this.currentMode);
  }

  disconnectedCallback() {
    this._abortController?.abort();
    this._initialized = false;
  }

  _onVariantUpdate(event) {
    const available = event.detail?.resource?.available;
    this._canAddToCart = available !== false;
    this._updateSubmitButtons();
  }

  _watchSubmitButtons() {
    const submitBtns = this.querySelectorAll('[data-instinct-form-submit]');
    if (!submitBtns.length) return;

    const observer = new MutationObserver(() => {
      submitBtns.forEach((btn) => {
        if (btn.disabled && this._canAddToCart) {
          btn.disabled = false;
        }
      });
    });

    submitBtns.forEach((btn) => {
      observer.observe(btn, { attributes: true, attributeFilter: ['disabled'] });
    });
  }

  _updateSubmitButtons() {
    this.querySelectorAll('[data-instinct-form-submit]').forEach((btn) => {
      btn.disabled = !this._canAddToCart;
    });
  }

  _onSelect(event) {
    const button = event.currentTarget;
    const mode = button.dataset.instinctSelect;
    if (!mode || mode === this.currentMode) return;
    this._syncMode(mode);
  }

  _onPlanChange(event) {
    if (this.currentMode === 'subscribe' && this.planInput) {
      this.planInput.value = event.target.value || '';
    }
  }

  _onQuantityChange(event) {
    if (this.quantityInput) {
      this.quantityInput.value = event.target.value || '1';
    }
    // Keep all quantity selects visually in sync
    this.quantitySelects.forEach((select) => {
      if (select !== event.target) {
        select.value = event.target.value;
      }
    });
  }

  _onFormSubmit(event) {
    event.preventDefault();

    const form = event.currentTarget;
    const submitBtns = this.querySelectorAll('[data-instinct-form-submit]');
    submitBtns.forEach((btn) => (btn.disabled = true));

    const formData = new FormData(form);

    // Collect cart-items section IDs so the response includes fresh section HTML
    const sectionIds = [];
    document.querySelectorAll('cart-items-component').forEach((item) => {
      if (item.dataset && item.dataset.sectionId) {
        sectionIds.push(item.dataset.sectionId);
      }
    });
    if (sectionIds.length) {
      formData.append('sections', sectionIds.join(','));
    }

    fetch(Theme.routes.cart_add_url, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: formData,
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.status) {
          console.error('Add to cart error:', data.message);
          return;
        }
        return fetch('/cart.js')
          .then((r) => r.json())
          .then((cart) => {
            this.dispatchEvent(
              new CustomEvent('cart:update', {
                bubbles: true,
                detail: {
                  resource: cart,
                  sourceId: this.id,
                  data: {
                    source: 'instinct-product-form',
                    sections: data.sections,
                  },
                },
              })
            );
          });
      })
      .catch((err) => {
        console.error('Cart add failed:', err);
      })
      .finally(() => {
        submitBtns.forEach((btn) => {
          btn.disabled = !this._canAddToCart;
        });
      });
  }

  _syncMode(mode) {
    this.currentMode = mode;

    this.cards.forEach((card) => {
      const isSelected = card.dataset.orderType === mode;
      card.dataset.selected = isSelected ? 'true' : 'false';
      const button = card.querySelector('[data-instinct-select]');
      if (button) button.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
    });

    if (this.planInput) {
      if (mode === 'subscribe') {
        const planValue = this.planSelect ? this.planSelect.value : this.defaultPlanId;
        this.planInput.value = planValue || this.defaultPlanId || '';
      } else {
        this.planInput.value = '';
      }
    }

    this._updateSubmitButtons();
  }
}

if (!customElements.get('instinct-product-form')) {
  customElements.define('instinct-product-form', InstinctProductForm);
}
